/**
 * PlacementCarrierChips — the carrier (Versicherer) chips in the Kopfbereich meta row. Filled by
 * Slice 7 from the Versicherer-Auswahl (ausgewaehlt + inaktiv rows, Ch 8.1 «ausgewählt»); visible on
 * every tab. Renders null when empty. `inaktiv` chips (set by Slice 8 once versandt-then-deaktiviert)
 * get an outline + strikethrough name.
 */

import { Badge } from '../../components/ui/Badge';
import { CarrierLogo } from './CarrierLogo';
import { dateShortDe } from '../../lib/format';
import { SUBMISSION_STATUS, VERSICHERER_AUSWAHL_STATUS } from '@brokly/shared';
import type { PlacementCarrierChip } from '@brokly/shared';

type BadgeVariant = 'neutral' | 'outline' | 'success' | 'warning' | 'danger' | 'info';

export function PlacementCarrierChips({ chips }: { chips: PlacementCarrierChip[] }) {
  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1" data-testid="placement-carrier-chips">
      {chips.map((c) => {
        // Slice-8 overlay: a submission status tints + relabels the chip; else the Slice-7 label.
        const inaktiv =
          c.submission_status === SUBMISSION_STATUS.INAKTIV || c.status === VERSICHERER_AUSWAHL_STATUS.INAKTIV;
        let variant: BadgeVariant = inaktiv ? 'outline' : 'neutral';
        if (c.submission_status === SUBMISSION_STATUS.NICHT_OFFERIERT) variant = 'danger';
        else if (c.frist_abgelaufen) variant = 'warning';
        return (
          <Badge key={c.carrier_uid} variant={variant} data-testid={`pcc-chip-${c.carrier_uid}`}>
            <CarrierLogo name={c.name} logoUrl={c.logo_url} size="sm" />
            <span className={inaktiv ? 'line-through' : undefined}>{c.name ?? c.carrier_uid}</span>
            <span className="text-muted-foreground">· {c.status_label}</span>
            {c.submission_status && c.frist_date && (
              <span className="text-muted-foreground">· bis {dateShortDe(c.frist_date)}</span>
            )}
          </Badge>
        );
      })}
    </div>
  );
}
